import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import hiveService from '../../services/hive';
import { getPostAppInfo } from '../../utils/postApp';
import { formatRelativeTime, formatAbsoluteTime } from '../../utils/time';
import './ArticleHeader.css';
import VoteSlider from '../VoteSlider/VoteSlider';
import VotersModal from '../VotersModal/VotersModal';
import Lightbox from '../Lightbox/Lightbox';
import useAuthorAvatar from '../../utils/useAuthorAvatar';
import usePostBlockchainData from '../../services/usePostBlockchainData';
import AddToListModal from '../AddToListModal/AddToListModal';
import BookmarkModal from '../BookmarkModal/BookmarkModal';
import { useLists } from '../../context/ListsContext';

const ArticleHeader = ({ post, readingTime, onShare, onScrollToComments, commentsCount, onReplyClick }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { isInFavourites, toggleFavourites, lists, loaded: listsLoaded } = useLists();
  const { addMutedUser, removeMutedUser, isUserMuted, showVoteSlider: globalShowVoteSlider, voteDefaultWeight, rewardDisplayMode, rewardDisplayCurrency } = useSettings();
  const isMountedRef = React.useRef(true);
  const [isVoting, setIsVoting] = useState(false);
  const [showVoteSlider, setShowVoteSlider] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Update relative time display every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTimerTick(prev => prev + 1);
    }, 30000); // Update every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  const [voteWeight, setVoteWeight] = useState(voteDefaultWeight || 100);

  const {
    voteCount, setVoteCount,
    hasVoted, setHasVoted,
    userVoteWeight,
    pendingRewardHive, pendingRewardHbd,
    totalPayoutHive, totalPayoutHbd,
    activeVotesWithValue,
    castVote
  } = usePostBlockchainData({
    post, user, isAuthenticated, rewardDisplayMode, rewardDisplayCurrency, initialReplyCount: commentsCount
  });

  const [isReblogging, setIsReblogging] = useState(false);
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [favouritesPending, setFavouritesPending] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showCopyNotice, setShowCopyNotice] = useState(false);
  const [copyNoticeMessage, setCopyNoticeMessage] = useState('Permlink copied');
  const menuRef = useRef(null);
  const copyNoticeTimeoutRef = useRef(null);
  const [showVotersModal, setShowVotersModal] = useState(false);
  const [showAuthorMenu, setShowAuthorMenu] = useState(false);
  const [showProfileImageLightbox, setShowProfileImageLightbox] = useState(false);
  const authorMenuRef = useRef(null);
  const [, setTimerTick] = useState(0); // Force re-renders for relative time updates
  const parentAuthor = String(post?.parentAuthor || post?.parent_author || '').trim();
  const depthValue = Number(post?.depth);
  const isArticle = Number.isFinite(depthValue) ? depthValue === 0 : !parentAuthor;

  const wordCount = (post.body || '').split(/\s+/).length;

  const metadata = useMemo(() => {
    const rawMetadata = post?.json_metadata ?? post?.metadata ?? post?.jsonMetadata ?? null;
    if (!rawMetadata) return null;
    if (typeof rawMetadata === 'string') {
      try {
        return JSON.parse(rawMetadata);
      } catch (error) {
        return null;
      }
    }
    return typeof rawMetadata === 'object' ? rawMetadata : null;
  }, [post]);

  const appInfo = useMemo(() => getPostAppInfo(post, metadata), [post, metadata]);

  const txId = useMemo(() => {
    if (!post) return null;
    if (post.tx_id) return post.tx_id;
    if (post.txId) return post.txId;
    return metadata?.tx_id || metadata?.txId || null;
  }, [post, metadata]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (authorMenuRef.current && !authorMenuRef.current.contains(event.target)) {
        setShowAuthorMenu(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        setShowAuthorMenu(false);
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
      if (copyNoticeTimeoutRef.current) {
        clearTimeout(copyNoticeTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyLink = useCallback(async () => {
    const postUrl = `@${post.author}/${post.permlink}`;
    let copied = false;

    try {
      await navigator.clipboard.writeText(postUrl);
      copied = true;
    } catch (error) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = postUrl;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        copied = document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch (fallbackError) {
        copied = false;
      }
    }

    setShowMenu(false);
    setCopyNoticeMessage(copied ? 'Permlink copied' : 'Unable to copy link');
    setShowCopyNotice(true);

    if (copyNoticeTimeoutRef.current) {
      clearTimeout(copyNoticeTimeoutRef.current);
    }

    copyNoticeTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setShowCopyNotice(false);
      }
      copyNoticeTimeoutRef.current = null;
    }, 1600);
  }, [post.author, post.permlink]);
  
  // Removed inline formatDate in favor of unified utility

  // Get all tags from metadata
  const allTags = Array.isArray(metadata?.tags) ? metadata.tags : [];

  // Determine the community slug if the post is in a community.  Hive
  // community tags always start with "hive-"; creators typically include the
  // slug both as the category and in the metadata tags.  We search the tags
  // first since that's what the user request refers to – "article-tags contain
  // the community tag" – but fall back to the category if it also matches.
  let communitySlug = null;
  const hiveTag = allTags.find(t => typeof t === 'string' && t.startsWith('hive-'));
  if (hiveTag) {
    communitySlug = hiveTag;
  } else if (post.category && typeof post.category === 'string' && post.category.startsWith('hive-')) {
    communitySlug = post.category;
  }

  // if we didn't receive a communityTitle from the server, fetch the info
  // ourselves so we can display the human-readable name (e.g. "HiveDevs").
  const [remoteCommunityTitle, setRemoteCommunityTitle] = useState(null);
  useEffect(() => {
    if (communitySlug && !post.communityTitle) {
      hiveService.getCommunityTitle(communitySlug)
        .then(title => {
          if (title) {
            setRemoteCommunityTitle(title);
            // if title looks like the slug, attempt a direct chain fetch
            if (title === communitySlug) {
              hiveService.client.call('bridge','get_community',{ name: communitySlug })
                .then(comm => {
                  if (comm?.title && comm.title !== communitySlug) {
                    setRemoteCommunityTitle(comm.title);
                  }
                })
                .catch(err => console.error('Fallback chain lookup failed', err));
            }
          }
        })
        .catch(err => {
          console.error('Error fetching community title:', err);
        });
    }
  }, [communitySlug, post?.communityTitle]);

  // Define all hooks BEFORE early return check
  const handleVote = useCallback(async (isUpvote = true, explicitWeight = null) => {
    if (!isMountedRef.current) return;
    if (!isAuthenticated) {
      alert('Please login to vote');
      return;
    }

    setIsVoting(true);
    try {
      const percent = explicitWeight !== null ? explicitWeight : voteWeight;
      await castVote(percent, isUpvote);
      if (!isMountedRef.current) return;
      setShowVoteSlider(false);
      setHasVoted(isUpvote ? true : false);
      setVoteCount(prev => prev + (isUpvote ? 1 : -1));
    } catch (error) {
      console.error('Vote error:', error);
      alert('Failed to vote: ' + error.message);
    } finally {
      if (isMountedRef.current) setIsVoting(false);
    }
  }, [isAuthenticated, voteWeight, castVote, setHasVoted, setVoteCount]);

  const handleVoteClick = useCallback(() => {
    if (!isAuthenticated) {
      alert('Please login to vote');
      return;
    }

    if (!globalShowVoteSlider) {
      const wt = voteDefaultWeight || 100;
      handleVote(true, wt);
      return;
    }

    setVoteWeight(userVoteWeight != null ? userVoteWeight : (voteDefaultWeight || 100));
    setShowVoteSlider(!showVoteSlider);
  }, [isAuthenticated, globalShowVoteSlider, voteDefaultWeight, userVoteWeight, showVoteSlider, handleVote]);

  const { avatarSources, handleAvatarError } = useAuthorAvatar({ author: post?.author, profileImage: post?.profileImage });
  const profileUrl = `/user/${post.author}/blog/posts`;

  if (!post) return null;

  const handleReblog = async () => {
    if (!isAuthenticated) {
      alert('Please login to reblog');
      return;
    }

    setIsReblogging(true);
    try {
      await hiveService.reblog(user.username, post.author, post.permlink);
      alert('Post reblogged successfully!');
    } catch (error) {
      console.error('Reblog error:', error);
      alert('Failed to reblog: ' + error.message);
    } finally {
      setIsReblogging(false);
    }
  };

  const openProfileImage = () => {
    setShowProfileImageLightbox(true);
    setShowAuthorMenu(false);
  };

  const openProfile = () => {
    navigate(profileUrl);
    setShowAuthorMenu(false);
  };

  return (
    <header className="article-header">
      <div className="article-header-content">
        {/* debug output showing post data, community and tags 
        <div className="article-debug">
          <pre style={{fontSize: '0.75rem', overflowX: 'auto', maxHeight: 200}}>
            {JSON.stringify({
              post,
              communitySlug,
              communityTitle: post.communityTitle || remoteCommunityTitle,
              remoteCommunityTitle,
              allTags
            }, null, 2)}
          </pre>
        </div>
        */}

        {/* Tags */}
        {(allTags.length > 0 || communitySlug) && (
          <div className="article-tags">
            {/* if we have a community slug among the tags, display the
                community title as a link and then render the literal tag */}
            {communitySlug && allTags.includes(communitySlug) && (
              <>
                <Link
                  to={`/c/${communitySlug}`}
                  className="article-tag article-community-title"
                >
                  {post.communityTitle || remoteCommunityTitle || communitySlug}
                </Link>
                <Link
                  to={`/tag/${communitySlug}`}
                  className="article-tag"
                >
                  #{communitySlug}
                </Link>
              </>
            )}

            {/* render remaining tags, excluding the community slug if we
                already showed it above */}
            {allTags.filter(t => t !== communitySlug).map((tag, i) => (
              <Link
                key={i}
                to={`/tag/${tag}`}
                className="article-tag"
              >
                #{tag}
              </Link>
            ))}
          </div>
        )}

        {/* Title */}
        <h1 className="article-title">{post.title}</h1>

        {/* Reading metadata */}
        <div className="article-meta">
          <span className="meta-item">
            <span className="meta-icon">📖</span>
            {readingTime} min read
          </span>
          <span className="meta-item">
            <span className="meta-icon">📝</span>
            {wordCount.toLocaleString()} words
          </span>
          <span className="meta-item" title={formatAbsoluteTime(post.created)}>
            <span className="meta-icon">⏰</span>
            {formatRelativeTime(post.created)}
          </span>
          {appInfo && (
            <span className="meta-item article-via-app" title={appInfo.key}>
              <span className="meta-icon">📡</span>
              via <em>{appInfo.name}</em>
            </span>
          )}
        </div>

        {/* Author info */}
        <div className="article-author">
          <div className="article-author-main">
            <div className="author-avatar-menu" ref={authorMenuRef}>
              <button
                type="button"
                className="author-avatar-trigger"
                onClick={() => setShowAuthorMenu((prev) => !prev)}
                aria-label="Open author avatar menu"
              >
                <img
                  src={avatarSources[0] || ''}
                  alt={`${post.author} avatar`}
                  className="author-avatar"
                  loading="lazy"
                  data-avatar-fallback-index="0"
                  onError={handleAvatarError}
                />
              </button>
              {showAuthorMenu && (
                <div className="author-avatar-dropdown">
                  <button type="button" onClick={openProfileImage}>View profile image</button>
                  <button type="button" onClick={openProfile}>View profile</button>
                </div>
              )}
            </div>
            <div className="author-info">
              <div className="author-name">
                By&nbsp;
                <Link to={profileUrl} className="author-name-link">
                  <strong>@{post.author}</strong>
                </Link>
              </div>
              <div className="author-votes">
                <span>{(post.net_votes || 0).toLocaleString()} votes</span>
              </div>
            </div>
          </div>
        </div>

        {/* Post Actions */}
        <div className="article-post-actions">
          <button 
            className="article-action-btn comment-btn"
            onClick={onScrollToComments}
            title="Jump to comments"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>{typeof commentsCount === 'number' ? commentsCount : (post.children || 0)}</span>
          </button>

          <button 
            className={`article-action-btn vote-btn ${hasVoted ? 'voted' : ''}`}
            onClick={handleVoteClick}
            disabled={isVoting}
            title={hasVoted ? 'You have already voted' : 'Upvote'}
          >
            <svg viewBox="0 0 24 24" fill={hasVoted ? "var(--accent-primary)" : "none"} stroke={hasVoted ? "var(--accent-primary)" : "currentColor"} strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            <span>{voteCount}</span>
          </button>

          {!(post?.depth > 0 || post?.parent_author) && (
            <button 
              className="article-action-btn reblog-btn"
              onClick={handleReblog}
              disabled={isReblogging}
              title="Reflux (Reblog)"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="17 1 21 5 17 9" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
              <span>Reflux</span>
            </button>
          )}

          {isAuthenticated && (
            <button
              className="article-action-btn add-to-list-btn"
              onClick={() => setShowAddToListModal(true)}
              title={`Add @${post.author} to list`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 6h13" />
                <path d="M8 12h13" />
                <path d="M8 18h13" />
                <path d="M3 12h.01" />
              </svg>
              <span>Add author to list</span>
            </button>
          )}

          {onReplyClick && (
            <button
              className="article-action-btn reply-btn"
              onClick={onReplyClick}
              title={isArticle ? 'Reply to article' : 'Reply to comment'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 10h10a8 8 0 0 1 8 8v2M3 10l6 6m-6-6 6-6" />
              </svg>
              <span>Reply</span>
            </button>
          )}

          {user?.username?.toLowerCase() === post?.author?.toLowerCase() &&
            post?.created &&
            (Date.now() - new Date(post.created).getTime()) < 7 * 24 * 60 * 60 * 1000 && (
            <button
              className="article-action-btn edit-btn"
              onClick={() => navigate(`/create-article?edit=true&author=${encodeURIComponent(post.author)}&permlink=${encodeURIComponent(post.permlink)}`)}
              title="Edit this article"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <span>Edit</span>
            </button>
          )}

          <button 
            className="article-action-btn reward-btn"
            onClick={() => setShowVotersModal(true)}
            title={(() => {
              const rewardBase = rewardDisplayCurrency === 'hbd' ? pendingRewardHbd : pendingRewardHive;
              if (rewardBase > 0) return '';
              const totalPaidOut = rewardDisplayCurrency === 'hbd' ? totalPayoutHbd : totalPayoutHive;
              if (totalPaidOut > 0) return `Paid out: ${ (rewardDisplayMode === 'author_only' ? (totalPaidOut * 0.5) : totalPaidOut).toFixed(3) } ${rewardDisplayCurrency === 'hbd' ? 'HBD' : 'HIVE' }`;
              return 'No pending rewards';
            })()}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="8" r="7" />
              <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
            </svg>
            {(() => {
              const rewardBase = rewardDisplayCurrency === 'hbd' ? pendingRewardHbd : pendingRewardHive;
              const base = rewardBase > 0 ? rewardBase : 0;
              const display = rewardDisplayMode === 'author_only' ? base * 0.5 : base;
              const unit = rewardDisplayCurrency === 'hbd' ? 'HBD' : 'HIVE';
              return base > 0 ? <span>{display.toFixed(3)} {unit}</span> : null;
            })()}
          </button>

          <div className="action-dropdown-wrapper" ref={menuRef}>
            <button
              className="article-action-btn dropdown-toggle"
              onClick={() => setShowMenu(prev => !prev)}
              title="More actions"
              aria-haspopup="true"
              aria-expanded={showMenu}
            >
              ⋯
            </button>
            {showMenu && (
              <div className="action-dropdown-menu">
                {user?.username?.toLowerCase() === post?.author?.toLowerCase() &&
                  post?.created &&
                  (Date.now() - new Date(post.created).getTime()) < 7 * 24 * 60 * 60 * 1000 && (
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setShowMenu(false);
                      navigate(`/create-article?edit=true&author=${encodeURIComponent(post.author)}&permlink=${encodeURIComponent(post.permlink)}`);
                    }}
                  >
                    Edit
                  </button>
                )}
                {user?.username && user?.username?.toLowerCase() !== post?.author?.toLowerCase() && listsLoaded && (
                  <button
                    className="dropdown-item"
                    disabled={favouritesPending}
                    onClick={async () => {
                      setFavouritesPending(true);
                      try { await toggleFavourites(post.author); } catch (e) {}
                      setFavouritesPending(false);
                    }}
                  >
                    {isInFavourites(post.author) ? '★ Remove from favourites' : '☆ Add to favourites'}
                  </button>
                )}
                {user?.username && user?.username?.toLowerCase() !== post?.author?.toLowerCase() && listsLoaded && lists.length > 0 && (
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setShowMenu(false);
                      setShowAddToListModal(true);
                    }}
                  >
                    ☰ Add to list
                  </button>
                )}
                {user?.username && (
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setShowMenu(false);
                      setShowBookmarkModal(true);
                    }}
                  >
                    🔖 Bookmark
                  </button>
                )}
                {user?.username && user?.username?.toLowerCase() !== post?.author?.toLowerCase() && (
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setShowMenu(false);
                      if (isUserMuted(post.author)) {
                        if (window.confirm(`Unmute @${post.author}?`)) {
                          removeMutedUser(post.author);
                        }
                      } else {
                        if (window.confirm(`Are you sure you want to mute user\n@${post.author}?`)) {
                          addMutedUser(post.author);
                        }
                      }
                    }}
                  >
                    {isUserMuted(post.author) ? 'Unmute user' : 'Mute user'}
                  </button>
                )}
                <Link
                  to={`/user/${post.author}`}
                  className="dropdown-item"
                  onClick={() => setShowMenu(false)}
                >
                  View profile
                </Link>
                <button className="copy-link-btn dropdown-item" onClick={handleCopyLink}>
                  Copy link
                </button>
                {txId && (
                  <a
                    href={`https://hivexplorer.com/tx/${txId}`}
                    className="dropdown-item"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open on Hivexplorer"
                    onClick={() => setShowMenu(false)}
                  >
                    Show Tx ID
                  </a>
                )}
              </div>
            )}
            {showCopyNotice && (
              <div className="copy-link-notice" role="status" aria-live="polite">
                {copyNoticeMessage}
              </div>
            )}
          </div>
        </div>
      </div>

      {showVoteSlider && (
        <VoteSlider
          voteWeight={voteWeight}
          setVoteWeight={setVoteWeight}
          onCancel={() => setShowVoteSlider(false)}
          onVote={(isUp, explicit) => handleVote(isUp, explicit)}
          isVoting={isVoting}
          showDownvote={false}
          userHasVoted={hasVoted}
          userVoteWeight={userVoteWeight}
        />
      )}

      {showVotersModal && (
        <VotersModal
          author={post.author}
          permlink={post.permlink}
          votesWithRewards={activeVotesWithValue}
          onClose={() => setShowVotersModal(false)}
        />
      )}

      {showAddToListModal && (
        <AddToListModal
          targetUsername={post.author}
          onClose={() => setShowAddToListModal(false)}
        />
      )}

      {showBookmarkModal && (
        <BookmarkModal
          author={post.author}
          permlink={post.permlink}
          onClose={() => setShowBookmarkModal(false)}
        />
      )}

      {showProfileImageLightbox && (
        <Lightbox
          images={[avatarSources[0] || '']}
          currentIndex={0}
          onClose={() => setShowProfileImageLightbox(false)}
        />
      )}
    </header>
  );
};

export default ArticleHeader;
